import type { ReactElement } from "react";
import { Button, Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { SyncStatus } from "~/graphql/supergraphEnums";
import { formatFullDateAndTimeWithLocalTimezone } from "~/utils/datetime";
import { describeBatchSyncStatus } from "../../../utils/glSyncStatus";
import type { EntityGlSyncDetail, GlSyncError } from "../../../utils/columnDefs";

type Props = {
  settings?: EntityGlSyncDetail | null;
  syncStatus?: string | null;
  syncError?: GlSyncError | null;
  onViewError?: (error: GlSyncError) => void;
};

/**
 * "Last JSQ sync" cell for a transaction-category (child) row in the entities grid.
 * Owns every category-level status:
 *  - the animated syncing step ("Consolidating GL transactions" / "Loading...") while a
 *    category is in flight, which replaces the static "Processing" label;
 *  - the per-category "Sync failed" + View link;
 *  - the settings-derived states (disabled / ready to start / last-sync timestamp /
 *    settings required).
 */
export const CategorySyncStatusCell = ({
  settings = null,
  syncStatus = null,
  syncError = null,
  onViewError,
}: Props): ReactElement => {
  const isSettingsAvailable = settings && typeof settings === "object";

  // Failed is terminal-with-error: keep the "Sync failed" + View treatment.
  if (syncStatus === SyncStatus.Failed) {
    return (
      <Flex alignItems="center" gap={8}>
        <Icon type="exclamation-circle" color={Colors.Danger600} variant="outline" />
        <Text style={{ color: Colors.Danger600 }}>Sync failed</Text>
        {syncError && (
          <Button
            variant="link"
            displayAsLink
            isInline
            onClick={() => onViewError?.(syncError)}
          >
            View
          </Button>
        )}
      </Flex>
    );
  }

  // Only a genuinely in-flight status takes over the cell. A terminal status such as
  // `completed` is NOT in flight (`describeBatchSyncStatus` returns no spinner for it), so
  // it falls through to the settings-derived display below (the last-sync timestamp) rather
  // than mislabeling as "Processing". The backend already collapses a completed category to
  // a null status; this just keeps the component correct for any stray terminal value.
  const inFlightStep = syncStatus ? describeBatchSyncStatus(syncStatus) : null;
  if (inFlightStep?.showSpinner) {
    return (
      <Flex alignItems="center" gap={8}>
        <LoadingIndicator size="small" />
        <Text style={{ color: Colors.Neutral800 }}>{inFlightStep.label}</Text>
      </Flex>
    );
  }

  if (isSettingsAvailable) {
    if (!settings.active) {
      // Has sync defined but not active.
      return (
        <Flex alignItems="center" gap={8}>
          <Icon type="ban" color={Colors.Neutral600} />
          <Text style={{ color: Colors.Neutral600 }}>Sync disabled</Text>
        </Flex>
      );
    }
    if (!settings.lastSyncAt) {
      // Never synced before, first time for this transaction category.
      return (
        <Flex alignItems="center" gap={8}>
          <Icon type="check" color={Colors.Success600} />
          <Text style={{ color: Colors.Success600 }}>Ready to start sync</Text>
        </Flex>
      );
    }
    // Has settings, synced before, not currently processing.
    const syncUserName = settings.lastSyncUserName || "Unknown User";
    return (
      <span>
        {`${formatFullDateAndTimeWithLocalTimezone(
          settings.lastSyncAt,
          "MMM d, yyyy h:mm a",
          true,
        )} by ${syncUserName}`}
      </span>
    );
  }

  // No settings row in entity_sync_settings for this category.
  return (
    <Flex alignItems="center" gap={8}>
      <Icon type="unlink" color={Colors.Warning500} />
      <Text style={{ color: Colors.Warning500 }}>Sync settings required</Text>
    </Flex>
  );
};
